import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { sendEmail } from '../utils/emailService';
import PDFPreview from './PDFPreview';
import { t, getLanguage } from '../utils/translations';
import { 
  saveLogoToStorage, 
  getLogoFromStorage, 
  removeLogoFromStorage,
  fileToBase64,
  validateImageFile 
} from '../utils/logoStorage';
import {
  saveBankInfoToStorage,
  getBankInfoFromStorage
} from '../utils/bankStorage';
import { formatCurrency } from '../utils/currencyFormatter';
import './ProposalForm.css';

const ProposalForm = () => {
  const navigate = useNavigate();
  
  // Default test values
  const defaultValues = {
    companyName: 'Selahattin Babadag',
    companyEmail: 'mababadag@gmail.com',
    companyAddress: 'Alavardı1 mah. islemeli sok No: 19/6',
    companyPhone: '+905324078125',
    clientName: 'AVM',
    clientEmail: 'cesbabadag@gmail.com',
    clientCompany: 'AVM GRUP A.Ş.',
    proposalTitle: 'Proje Hizmeti',
    proposalDescription: 'Fransa statik projelerin yapılması',
    proposalNumber: '1',
    validUntil: '2027-01-01',
    terms: 'Pay after done',
    // Bank info fields
    bankName: '',
    accountHolderName: '',
    accountNumber: '',
    iban: '',
    swift: '',
    branchName: ''
  };
  
  // Load saved bank info and merge with defaults
  const savedBankInfo = getBankInfoFromStorage();
  if (savedBankInfo) {
    Object.assign(defaultValues, savedBankInfo);
  }
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: defaultValues
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [proposalData, setProposalData] = useState(null);
  const [logo, setLogo] = useState(null);
  const [logoError, setLogoError] = useState(null);
  const [items, setItems] = useState([
    { description: 'Statik Proje A', quantity: 1, unit: 'ton', price: 1000 },
    { description: 'Statik Proje B', quantity: 1, unit: 'ton', price: 1200 }
  ]);

  // Load logo and bank info from localStorage on component mount
  useEffect(() => {
    const savedLogo = getLogoFromStorage();
    if (savedLogo) {
      setLogo(savedLogo);
    }
    
    // Load and display saved bank info in form
    const savedBankInfo = getBankInfoFromStorage();
    if (savedBankInfo) {
      // Reset form with saved bank info merged into defaults
      reset({
        ...defaultValues,
        ...savedBankInfo
      });
    }
  }, [reset]);
  
  // Track language for re-rendering (including currency updates)
  const [currentLang, setCurrentLang] = useState(getLanguage());
  
  useEffect(() => {
    const checkLanguage = () => {
      const lang = getLanguage();
      if (lang !== currentLang) {
        setCurrentLang(lang);
        // Force re-render to update currency formatting
        setItems([...items]);
      }
    };
    const interval = setInterval(checkLanguage, 200);
    return () => clearInterval(interval);
  }, [currentLang]);

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unit: 'ton', price: 0 }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = field === 'quantity' || field === 'price' ? parseFloat(value) || 0 : value;
    setItems(updatedItems);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLogoError(null);

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setLogoError(validation.error);
      return;
    }

    try {
      // Convert to base64
      const base64 = await fileToBase64(file);
      
      // Save to localStorage
      if (saveLogoToStorage(base64)) {
        setLogo(base64);
        setLogoError(null);
      } else {
        setLogoError('Failed to save logo. Please try again.');
      }
    } catch (error) {
      console.error('Error processing logo:', error);
      setLogoError('Failed to process image. Please try again.');
    }
  };

  const handleRemoveLogo = () => {
    if (removeLogoFromStorage()) {
      setLogo(null);
      setLogoError(null);
      // Reset file input
      const fileInput = document.getElementById('logo-upload');
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Save bank info to localStorage
      const bankInfo = {
        bankName: data.bankName || '',
        accountHolderName: data.accountHolderName || '',
        accountNumber: data.accountNumber || '',
        iban: data.iban || '',
        swift: data.swift || '',
        branchName: data.branchName || ''
      };
      saveBankInfoToStorage(bankInfo);

      const preparedData = {
        ...data,
        items: items,
        total: calculateTotal(),
        logo: logo, // Include logo in proposal data
        bankInfo: bankInfo, // Include bank info in proposal data
        date: new Date().toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })
      };

      // Show preview instead of immediately sending
      setProposalData(preparedData);
      setShowPreview(true);
      setIsSubmitting(false);
    } catch (err) {
      setError(err.message || 'Failed to prepare proposal. Please try again.');
      console.error('Error:', err);
      setIsSubmitting(false);
    }
  };

  const handlePreviewConfirm = async (pdfBlob) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Send email with PDF
      await sendEmail({
        to: proposalData.clientEmail,
        subject: `Proposal from ${proposalData.companyName}`,
        body: `Dear ${proposalData.clientName},\n\nPlease find attached the proposal for your review.\n\nBest regards,\n${proposalData.companyName}`,
        pdfBlob: pdfBlob,
        pdfFileName: `Proposal_${proposalData.clientName}_${Date.now()}.pdf`
      });

      setShowPreview(false);
      navigate('/success');
    } catch (err) {
      setError(err.message || 'Failed to send email. Please try again.');
      console.error('Error:', err);
      setIsSubmitting(false);
    }
  };

  const handlePreviewCancel = () => {
    setShowPreview(false);
    setProposalData(null);
  };

  return (
    <>
      {showPreview && proposalData && (
        <PDFPreview
          proposalData={proposalData}
          onConfirm={handlePreviewConfirm}
          onCancel={handlePreviewCancel}
        />
      )}
      <div className="proposal-form-container">
        <form onSubmit={handleSubmit(onSubmit)} className="proposal-form">
        <div className="form-section">
          <h2>{t('companyInformation')}</h2>
          
          {/* Logo Upload Section */}
          <div className="logo-upload-section">
            <label className="logo-upload-label">{t('companyLogo')}</label>
            <div className="logo-upload-container">
              {logo ? (
                <div className="logo-preview-container">
                  <img src={logo} alt="Company Logo" className="logo-preview" />
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="btn-remove-logo"
                    title="Remove logo"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="logo-placeholder">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  <p>{t('noLogoUploaded')}</p>
                </div>
              )}
              <div className="logo-upload-controls">
                <label htmlFor="logo-upload" className="btn-upload-logo">
                  {logo ? t('changeLogo') : t('uploadLogo')}
                </label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
            {logoError && <span className="error">{logoError}</span>}
            <p className="logo-hint">{t('logoHint')}</p>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('companyName')} *</label>
              <input
                type="text"
                {...register('companyName', { required: `${t('companyName')} ${t('required')}` })}
                placeholder={t('companyName')}
              />
              {errors.companyName && <span className="error">{errors.companyName.message}</span>}
            </div>
            <div className="form-group">
              <label>{t('companyEmail')} *</label>
              <input
                type="email"
                {...register('companyEmail', { 
                  required: `${t('companyEmail')} ${t('required')}`,
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('invalidEmail')
                  }
                })}
                placeholder={t('companyEmail')}
              />
              {errors.companyEmail && <span className="error">{errors.companyEmail.message}</span>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>{t('companyAddress')}</label>
              <textarea
                {...register('companyAddress')}
                placeholder={t('companyAddress')}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>{t('companyPhone')}</label>
              <input
                type="tel"
                {...register('companyPhone')}
                placeholder={t('companyPhone')}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>{t('clientInformation')}</h2>
          <div className="form-row">
            <div className="form-group">
              <label>{t('clientName')} *</label>
              <input
                type="text"
                {...register('clientName', { required: `${t('clientName')} ${t('required')}` })}
                placeholder={t('clientName')}
              />
              {errors.clientName && <span className="error">{errors.clientName.message}</span>}
            </div>
            <div className="form-group">
              <label>{t('clientEmail')} *</label>
              <input
                type="email"
                {...register('clientEmail', { 
                  required: `${t('clientEmail')} ${t('required')}`,
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('invalidEmail')
                  }
                })}
                placeholder={t('clientEmail')}
              />
              {errors.clientEmail && <span className="error">{errors.clientEmail.message}</span>}
            </div>
          </div>
          <div className="form-group">
            <label>{t('clientCompany')}</label>
            <input
              type="text"
              {...register('clientCompany')}
              placeholder={t('clientCompany')}
            />
          </div>
        </div>

        <div className="form-section">
          <h2>{t('proposalDetails')}</h2>
          <div className="form-group">
            <label>{t('proposalTitle')} *</label>
            <input
              type="text"
              {...register('proposalTitle', { required: `${t('proposalTitle')} ${t('required')}` })}
              placeholder={t('proposalTitle')}
            />
            {errors.proposalTitle && <span className="error">{errors.proposalTitle.message}</span>}
          </div>
          <div className="form-group">
            <label>{t('proposalDescription')}</label>
            <textarea
              {...register('proposalDescription')}
              placeholder={t('proposalDescription')}
              rows="4"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>{t('validUntil')}</label>
              <input
                type="date"
                {...register('validUntil')}
              />
            </div>
            <div className="form-group">
              <label>{t('proposalNumber')}</label>
              <input
                type="text"
                {...register('proposalNumber')}
                placeholder={t('proposalNumber')}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="items-header">
            <h2>{t('itemsPricing')}</h2>
            <button type="button" onClick={addItem} className="btn-add-item">
              + {t('addItem')}
            </button>
          </div>
          <div className="items-list">
            <div className="item-row-header">
              <div className="item-description-header">{t('description')}</div>
              <div className="item-quantity-header">{t('quantity')}</div>
              <div className="item-unit-header">{t('unit')}</div>
              <div className="item-price-header">{t('unitPrice')}</div>
              <div className="item-total-header">{t('total')}</div>
              <div className="item-action-header"></div>
            </div>
            {items.map((item, index) => (
              <div key={`${index}-${currentLang}`} className="item-row">
                <div className="item-description">
                  <input
                    type="text"
                    placeholder={t('description')}
                    value={item.description}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                  />
                </div>
                <div className="item-quantity">
                  <input
                    type="number"
                    min="1"
                    placeholder={t('quantity')}
                    value={item.quantity}
                    onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                  />
                </div>
                <div className="item-unit">
                  <input
                    type="text"
                    placeholder={t('unit')}
                    value={item.unit || 'ton'}
                    onChange={(e) => updateItem(index, 'unit', e.target.value)}
                  />
                </div>
                <div className="item-price">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={t('unitPrice')}
                    value={item.price}
                    onChange={(e) => updateItem(index, 'price', e.target.value)}
                  />
                </div>
                <div className="item-total">
                  {formatCurrency(item.quantity * item.price)}
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="btn-remove"
                  disabled={items.length === 1}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <div className="total-section">
            <div className="total-label">{t('totalAmount')}:</div>
            <div className="total-amount">{formatCurrency(calculateTotal())}</div>
          </div>
        </div>

        <div className="form-section">
          <h2>{t('bankInformation')}</h2>
          <p className="section-hint">{t('bankInfoHint')}</p>
          <div className="form-row">
            <div className="form-group">
              <label>{t('bankName')}</label>
              <input
                type="text"
                {...register('bankName')}
                placeholder={t('bankName')}
              />
            </div>
            <div className="form-group">
              <label>{t('accountHolderName')}</label>
              <input
                type="text"
                {...register('accountHolderName')}
                placeholder={t('accountHolderName')}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>{t('accountNumber')}</label>
              <input
                type="text"
                {...register('accountNumber')}
                placeholder={t('accountNumber')}
              />
            </div>
            <div className="form-group">
              <label>{t('iban')}</label>
              <input
                type="text"
                {...register('iban')}
                placeholder={t('iban')}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>{t('swift')}</label>
              <input
                type="text"
                {...register('swift')}
                placeholder={t('swift')}
              />
            </div>
            <div className="form-group">
              <label>{t('branchName')}</label>
              <input
                type="text"
                {...register('branchName')}
                placeholder={t('branchName')}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>{t('additionalNotes')}</h2>
          <div className="form-group">
            <label>{t('termsConditions')}</label>
            <textarea
              {...register('terms')}
              placeholder={t('termsConditions')}
              rows="4"
            />
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="btn-submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Preparing Preview...' : 'Preview & Send Proposal'}
          </button>
        </div>
      </form>
    </div>
    </>
  );
};

export default ProposalForm;
